import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import CustomTable from '../../components/customtable/CustomTable';
import DetailCard from '../../components/detailCard/DetailCard';
import Loader from '../../components/loader/Loader';
import Nudge from '../../components/nudge/Nudge';
import APPCONSTANTS from '../../constants/appConstants';
import ERRORS from '../../constants/error';
import { PROTECTED_ROUTES } from '../../constants/route';
import sessionStorageServices from '../../global/sessionStorageServices';
import { clearSiteDetail, fetchSiteListRequest, setSiteDetail } from '../../store/site/actions';
import {
  getSiteListSelector,
  getSiteListTotalSelector,
  getSiteLoadingSelector,
  nudgeLoadingSelector
} from '../../store/site/selectors';
import { roleSelector } from '../../store/user/selectors';
import toastCenter from '../../utils/toastCenter';

interface IMatchParams {
  regionId: string;
  regionName: string;
}

const SiteList = (props: IMatchParams): React.ReactElement => {
  const [listParams, setListReqParams] = useState({
    page: 1,
    rowsPerPage: APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE,
    searchTerm: ''
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const userRole = useSelector(roleSelector);

  // Selectors
  const sites = useSelector(getSiteListSelector);
  const loading = useSelector(getSiteLoadingSelector);
  const siteCount = useSelector(getSiteListTotalSelector);
  const nudgeLoading = useSelector(nudgeLoadingSelector);

  const [openModal, setOpenModal] = useState({ isOpen: false, data: {} as any });
  const { regionId, regionName } = useParams<IMatchParams>();

  // Only show the site list loader if it's loading AND nudge is not loading
  const showSiteListLoader = loading && !nudgeLoading;

  useEffect(() => {
    dispatch(
      fetchSiteListRequest({
        regionId: Number(regionId),
        skip: (listParams.page - 1) * listParams.rowsPerPage,
        limit: listParams.rowsPerPage,
        search: listParams.searchTerm,
        isHF4User: userRole === APPCONSTANTS.ROLES.HF4_REPORT_USER,
        userBased: userRole === 'REPORT_ADMIN' ? false : true,
        failureCb: (e) => {
          if (e.message === ERRORS.NETWORK_ERROR.message) {
            toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
          } else {
            toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.SITE_FETCH_ERROR);
          }
        }
      })
    );
  }, [listParams, regionId, dispatch, userRole]);

  useEffect(() => {
    dispatch(clearSiteDetail());
    sessionStorageServices.deleteItems([APPCONSTANTS.CHW_USER_ID, APPCONSTANTS.SITE_TENANT_ID]);
  }, [dispatch]);

  const handleSearch = (searchString: string) => {
    setListReqParams((prevstate) => ({
      ...prevstate,
      page: 1,
      searchTerm: searchString
    }));
  };

  const handlePage = (pageNo: number, rowsPerPage: number | undefined = APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE) => {
    setListReqParams((prevstate) => ({
      ...prevstate,
      page: pageNo,
      rowsPerPage
    }));
  };

  const handleUserSelection = (data: any, isFromUserView: boolean = false) => {
    const { id: userId } = data;
    const { id: siteId, tenantId: siteTenantId, name: siteName } = openModal.data.id ? openModal.data : data;

    sessionStorageServices.setItem(APPCONSTANTS.SITE_TENANT_ID, siteTenantId);
    sessionStorageServices.setItem(APPCONSTANTS.SITE_ID, siteId);
    const siteDetails = {
      name: siteName,
      id: siteId,
      tenantId: siteTenantId,
      ...(isFromUserView && { userId })
    };

    if (isFromUserView) {
      sessionStorageServices.setItem(APPCONSTANTS.CHW_USER_ID, userId);
    }

    dispatch(setSiteDetail(siteDetails));
    history.push(
      PROTECTED_ROUTES.reportList
        .replace(':regionId', regionId)
        .replace(':regionName', regionName)
        .replace(':siteId', siteId)
        .replace(':siteName', siteName.replace(/\//g, ''))
        .replace(':tenantId', siteTenantId)
    );
    setOpenModal({ isOpen: false, data: {} });
  };

  return (
    <div className='site-list-container'>
      <Nudge />
      {showSiteListLoader && <Loader />}
      <div className='col-12'>
        <DetailCard header='Sites' isSearch={true} onSearch={handleSearch}>
          <CustomTable
            rowData={sites}
            columnsDef={[
              {
                id: 1,
                name: 'name',
                label: 'Name'
              },
              {
                id: 2,
                name: 'cityName',
                label: 'City/Town'
              },
              {
                id: 3,
                name: 'phuFocalPersonNumber',
                label: 'Phone Number',
                class: 'numeric'
              }
            ]}
            isDelete={false}
            isEdit={false}
            isView={true}
            viewToolTip='Site View'
            isDownload={false}
            page={listParams.page}
            rowsPerPage={listParams.rowsPerPage}
            count={siteCount}
            isRowEdit={true}
            onRowView={handleUserSelection}
            handleRowClick={handleUserSelection}
            handlePageChange={handlePage}
          />
        </DetailCard>
      </div>
    </div>
  );
};

export default SiteList;
